import { useState, useEffect, lazy, Suspense } from 'react'
import { auth, db } from '@/lib/firebase'
import { getIdTokenResult, onAuthStateChanged, signOut } from 'firebase/auth'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom'
import { HomePage } from '@/components/pages/HomePage'
import { LoginPage } from '@/components/pages/LoginPage'
import { Navigation } from '@/components/Navigation'
import { SiteLaunchGate } from '@/components/SiteLaunchGate'
import { Toaster } from 'sonner'
import { DEMO_SERVICE_TYPES, type DemoServiceKey } from '@/lib/demoData'

// Lazy-loaded pages for code splitting
const Dashboard = lazy(() => import('@/components/pages/Dashboard').then(m => ({ default: m.Dashboard })))
const ContractorPage = lazy(() => import('@/components/pages/ContractorPage').then(m => ({ default: m.ContractorPage })))
const SearchResults = lazy(() => import('@/components/pages/SearchResults').then(m => ({ default: m.SearchResults })))
const ServicesPage = lazy(() => import('@/components/pages/ServicesPage').then(m => ({ default: m.ServicesPage })))
const ClaimPage = lazy(() => import('@/components/pages/ClaimPage').then(m => ({ default: m.ClaimPage })))
const VerifyPage = lazy(() => import('@/components/pages/VerifyPage').then(m => ({ default: m.VerifyPage })))
const HowItWorksPage = lazy(() => import('@/components/pages/HowItWorksPage').then(m => ({ default: m.HowItWorksPage })))
const BecomeContractorPage = lazy(() => import('@/components/pages/BecomeContractorPage').then(m => ({ default: m.BecomeContractorPage })))
const AboutPage = lazy(() => import('@/components/pages/AboutPage').then(m => ({ default: m.AboutPage })))
const ContactPage = lazy(() => import('@/components/pages/ContactPage').then(m => ({ default: m.ContactPage })))
const TermsPage = lazy(() => import('@/components/pages/TermsPage').then(m => ({ default: m.TermsPage })))
const PrivacyPage = lazy(() => import('@/components/pages/PrivacyPage').then(m => ({ default: m.PrivacyPage })))
const CookiesPage = lazy(() => import('@/components/pages/CookiesPage').then(m => ({ default: m.CookiesPage })))
const CostCalculatorPage = lazy(() => import('@/components/pages/CostCalculatorPage').then(m => ({ default: m.CostCalculatorPage })))
const FAQPage = lazy(() => import('@/components/pages/FAQPage').then(m => ({ default: m.FAQPage })))
const ContractorPortal = lazy(() => import('@/components/pages/ContractorPortal').then(m => ({ default: m.ContractorPortal })))
const BrowseStatesPage = lazy(() => import('@/components/pages/BrowseStatesPage').then(m => ({ default: m.BrowseStatesPage })))
const BrowseStatePage = lazy(() => import('@/components/pages/BrowseStatePage').then(m => ({ default: m.BrowseStatePage })))
const BrowseCountyPage = lazy(() => import('@/components/pages/BrowseCountyPage').then(m => ({ default: m.BrowseCountyPage })))
const CityLandingPage = lazy(() => import('@/components/pages/CityLandingPage').then(m => ({ default: m.CityLandingPage })))
const NotFoundPage = lazy(() => import('@/components/pages/NotFoundPage').then(m => ({ default: m.NotFoundPage })))
const AdminDashboard = lazy(() => import('@/components/pages/AdminDashboard').then(m => ({ default: m.AdminDashboard })))
const ServiceRequestForm = lazy(() => import('@/components/ServiceRequestForm').then(m => ({ default: m.ServiceRequestForm })))

export type User = {
  id: string
  email: string
  name: string
  isContractor: boolean
  zipCode?: string
}

export type UserWithLevel = User & {
  level?: number
  isAdmin?: boolean
  claimsLoaded?: boolean
}

// Public/live is the safe default for production builds.
// Set VITE_SITE_LIVE=false only when intentionally testing the locked splash.
const SITE_LIVE = import.meta.env.VITE_SITE_LIVE
  ? import.meta.env.VITE_SITE_LIVE === 'true'
  : !import.meta.env.DEV

const PREVIEW_UNLOCK_KEY = 'sealcoat-launch-unlocked'

function toDemoServiceKey(value: string | null): DemoServiceKey | undefined {
  return DEMO_SERVICE_TYPES.some((service) => service.key === value)
    ? value as DemoServiceKey
    : undefined
}

function App() {
  const [currentUser, setCurrentUser] = useState<UserWithLevel | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [previewUnlocked, setPreviewUnlocked] = useState(() => {
    try {
      return window.sessionStorage.getItem(PREVIEW_UNLOCK_KEY) === 'true'
    } catch {
      return false
    }
  })

  const navigate = useNavigate()
  const location = useLocation()

  const currentLevel = currentUser?.level ?? 0
  const isAdmin = Boolean(currentUser?.isAdmin || currentLevel >= 4)
  const canAccessAdmin = isAdmin

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userRef = doc(db, "users", firebaseUser.uid);
        const userSnap = await getDoc(userRef);
        // default level: 1 (logged-in free account)
        let level = 1
        let isAdminClaim = false

        if (userSnap.exists()) {
          const data = userSnap.data() as Record<string, unknown>
          const loaded: UserWithLevel = {
            id: firebaseUser.uid,
            email: firebaseUser.email || '',
            name: firebaseUser.displayName || (data.name as string || ''),
            isContractor: !!data.isContractor,
            zipCode: data.zipCode as string | undefined,
            level
          }
          setCurrentUser(loaded)
        } else {
          // create a minimal user record
          const newUser: UserWithLevel = {
            id: firebaseUser.uid,
            email: firebaseUser.email || "",
            name: firebaseUser.displayName || "",
            isContractor: false,
            level: 1
          }

          await setDoc(userRef, { ...newUser, createdAt: new Date().toISOString() }, { merge: true })
          setCurrentUser(newUser)
        }

        try {
          const token = await getIdTokenResult(firebaseUser, true)
          const tokenLevel = typeof token.claims.level === 'number' ? token.claims.level : undefined
          isAdminClaim = Boolean(token.claims.isAdmin || (tokenLevel !== undefined && tokenLevel >= 4))
          level = tokenLevel ?? level
        } catch (e) {
          console.warn('Failed to load auth claims:', e)
        }

        setCurrentUser((current) => {
          if (!current) {
            return current
          }
          return {
            ...current,
            level,
            isAdmin: isAdminClaim,
            claimsLoaded: true
          }
        })
      } else {
        setCurrentUser(null)
      }
      setAuthLoading(false)
    })

    if (!SITE_LIVE) {
      try {
        window.sessionStorage.removeItem(PREVIEW_UNLOCK_KEY)
      } catch {
        // Continue for privacy-restricted browser contexts.
      }
      setPreviewUnlocked(false)
    }

    return () => unsubscribe();
  }, []);

  // Backward compatibility shim for prop drilling throughout the old components
  // To avoid breaking 20 files that use "setCurrentPage('home')" today.
  const handleSetCurrentPage = (page: string) => {
    window.scrollTo(0, 0)
    switch(page) {
      case 'home': navigate('/'); break;
      case 'login': navigate('/login'); break;
      case 'dashboard': navigate('/dashboard'); break;
      // We assume selectedContractor is passed via URL or state now
      case 'contractor': navigate('/contractors'); break;
      case 'search': navigate('/search'); break;
      case 'services': navigate('/services'); break;
      case 'how-it-works': navigate('/how-it-works'); break;
      case 'become-contractor': navigate('/become-contractor'); break;
      case 'about': navigate('/about'); break;
      case 'contact': navigate('/contact'); break;
      case 'terms': navigate('/terms'); break;
      case 'privacy': navigate('/privacy'); break;
      case 'cookies': navigate('/cookies'); break;
      case 'cost-calculator': navigate('/cost-calculator'); break;
      case 'faq': navigate('/faq'); break;
      case 'browse-states': navigate('/states'); break;
      case 'contractor-portal': navigate('/admin/contractor'); break;
      case 'admin-dashboard': navigate('/admin/dashboard'); break;
      default: navigate('/');
    }
  }

  // Derive currentPage from the URL for the navigation bar highlights
  const getCurrentPageFromUrl = () => {
    const path = location.pathname;
    if (path.startsWith('/admin')) return 'contractor-portal';
    if (path.startsWith('/demo')) return 'home';
    if (path.includes('/dashboard')) return 'dashboard';
    if (path.includes('/login')) return 'login';
    if (path.includes('/search')) return 'search';
    if (path.includes('/services')) return 'services';
    if (path.includes('/how-it-works')) return 'how-it-works';
    if (path.includes('/cost-calculator')) return 'cost-calculator';
    if (path.includes('/faq')) return 'faq';
    if (path.includes('/become-contractor')) return 'become-contractor';
    if (path.includes('/about')) return 'about';
    if (path.includes('/contact')) return 'contact';
    if (path.includes('/terms')) return 'terms';
    if (path.includes('/privacy')) return 'privacy';
    if (path.includes('/cookies')) return 'cookies';
    if (path.includes('/claim')) return 'claim';
    if (path.includes('/verify')) return 'verify';
    if (path.startsWith('/states')) return 'browse-states';
    return 'home';
  }

  const navigateToSearch = (zip: string, service: string) => {
    navigate(`/search?zip=${encodeURIComponent(zip)}&service=${encodeURIComponent(service)}`)
  }

  const navigateToContractor = (username: string) => {
    navigate(`/${username}`)
  }

  // Safe "back" that falls back to home if there's no history (direct URL access)
  const safeBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }
  const isDemoRoute = location.pathname.startsWith('/demo/')
  const showLaunchGate = !SITE_LIVE && !isDemoRoute && !previewUnlocked
  return (
    showLaunchGate ? <SiteLaunchGate onUnlock={() => {
      try {
        window.sessionStorage.setItem(PREVIEW_UNLOCK_KEY, 'true')
      } catch {
        // Continue for privacy-restricted browser contexts.
      }
      setPreviewUnlocked(true)
    }} onDemoSearch={(zip, service) => {
      navigate(`/demo/request?${zip ? `zip=${encodeURIComponent(zip)}` : ''}${service ? `${zip ? '&' : ''}service=${encodeURIComponent(service)}` : ''}`)
    }} /> : (
    <div className="min-h-screen bg-background text-foreground">
      {authLoading ? (
        <div className="flex items-center justify-center min-h-screen">
          <div className="flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
            <p className="text-muted-foreground text-sm">Loading...</p>
          </div>
        </div>
      ) : (
      <>
      <Navigation 
        currentPage={getCurrentPageFromUrl()} 
        onNavigate={handleSetCurrentPage}
        currentUser={currentUser}
        onLockPreview={undefined}
        onLogout={() => {
          signOut(auth)
          navigate('/')
        }}
      />
      
      <main className="pb-16 md:pb-0">
        <Suspense fallback={<div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>}>
        <Routes>
          <Route path="/" element={
            <HomePage 
              onLogin={() => navigate('/login')}
              onSearch={navigateToSearch}
              onLockSite={!SITE_LIVE ? () => {
                try {
                  window.sessionStorage.removeItem(PREVIEW_UNLOCK_KEY)
                } catch {
                  // Continue for privacy-restricted browser contexts.
                }
                setPreviewUnlocked(false)
                navigate('/')
                window.scrollTo(0, 0)
              } : undefined}
              onNavigate={handleSetCurrentPage}
            />
          } />
          
          <Route path="/login" element={
            <LoginPage 
              onLogin={(user) => {
                setCurrentUser(user)
                navigate('/dashboard')
              }}
              onBack={() => navigate('/')}
            />
          } />

          <Route path="/dashboard" element={
            currentUser ? (
              <Dashboard 
                user={currentUser}
                onSearch={navigateToSearch}
                onViewContractor={navigateToContractor}
              />
            ) : <LoginPage onLogin={(user) => { setCurrentUser(user); navigate('/dashboard') }} onBack={() => navigate('/')} />
          } />

          {/* Dynamic SEO Zip/Service search route */}
          <Route path="/search" element={
            <SearchResults 
              zipCode={new URLSearchParams(location.search).get('zip') || ""}
              service={new URLSearchParams(location.search).get('service') || ""}
              onViewContractor={navigateToContractor}
              onBack={safeBack}
            />
          } />
          
          <Route path="/services" element={<ServicesPage onBack={safeBack} onGetStarted={() => navigate('/become-contractor')} />} />
          <Route path="/claim" element={<ClaimPage />} />
          <Route path="/verify" element={<VerifyPage />} />
          <Route path="/how-it-works" element={<HowItWorksPage onBack={safeBack} onGetStarted={() => navigate('/become-contractor')} />} />
          <Route path="/become-contractor" element={<BecomeContractorPage onBack={safeBack} onLogin={() => navigate('/login')} />} />
          <Route path="/about" element={<AboutPage onBack={safeBack} onGetStarted={() => navigate('/become-contractor')} />} />
          <Route path="/contact" element={<ContactPage onBack={safeBack} />} />
          <Route path="/terms" element={<TermsPage onBack={safeBack} />} />
          <Route path="/privacy" element={<PrivacyPage onBack={safeBack} />} />
          <Route path="/cookies" element={<CookiesPage onBack={safeBack} />} />
          <Route path="/cost-calculator" element={<CostCalculatorPage onBack={safeBack} onGetQuote={() => navigate('/become-contractor')} />} />
          <Route path="/faq" element={<FAQPage onBack={safeBack} onNavigate={handleSetCurrentPage} />} />
          
          {/* Browse by location */}
          <Route path="/states" element={<BrowseStatesPage />} />
          <Route path="/states/:stateSlug" element={<BrowseStatePage />} />
          <Route path="/states/:stateSlug/:countySlug" element={<BrowseCountyPage />} />
          <Route path="/states/:stateSlug/:countySlug/:citySlug/:zipCode" element={<CityLandingPage />} />
          <Route path="/states/:stateSlug/:countySlug/:citySlug" element={<CityLandingPage />} />

          {/* Demo service request form (accessible from splash page) */}
          <Route path="/demo/request" element={
              <ServiceRequestForm
                isDemo
                initialZip={new URLSearchParams(location.search).get('zip') || undefined}
              initialService={toDemoServiceKey(new URLSearchParams(location.search).get('service'))}
                onBack={() => navigate('/')}
              />
            } />

          {/* Admin dashboard — level 5 only */}
          <Route path="/admin/dashboard" element={
            currentUser && canAccessAdmin ? (
              <AdminDashboard
                user={currentUser}
                onBack={() => navigate('/dashboard')}
              />
            ) : <LoginPage onLogin={(user) => { setCurrentUser(user); navigate('/admin/dashboard') }} onBack={() => navigate('/')} />
          } />
          
          <Route path="/admin/contractor" element={
            currentUser ? (
              <ContractorPortal
                user={currentUser}
                onBack={() => navigate('/dashboard')}
              />
            ) : <LoginPage onLogin={(user) => { setCurrentUser(user); navigate('/admin/contractor') }} onBack={() => navigate('/')} />
          } />

          {/* Fallback route: treats the path as a Contractor Profile slug for SEO (/pacific-northwest-sealcoating) */}
          <Route path="/:contractorId" element={
            <ContractorPageWrapper 
              onBack={safeBack}
            />
          } />

          {/* 404 catch-all */}
          <Route path="*" element={<NotFoundPage />} />

        </Routes>
        </Suspense>
      </main>

      <Toaster position="bottom-right" />
      </>
      )}
    </div>
    )
  )
}

// Wrapper to bridge react-router's useParams into the old ContractorPage props
import { useParams } from 'react-router-dom';
function ContractorPageWrapper({ onBack }: { onBack: () => void }) {
  const { contractorId } = useParams();
  
  if (!contractorId) return null;

  return (
    <ContractorPage 
      username={contractorId}
      onBack={onBack}
    />
  )
}

export default App;
